import AttachFileIcon from '@mui/icons-material/AttachFile';
import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
} from '@mui/material';
import { useRef, useState } from 'react';

import { PillRadioGroup } from '~/components/common/PillRadioGroup';
import { formatFileSize } from '~/components/documentReview/useDocumentUpload';
import { EVIDENCE_ACCEPT, evidenceFileError } from '~/components/tasks/detail/evidenceFiles';
import { formatDate, STATUS_COLOR } from '~/components/tasks/taskStyle';
import type { TaskDetail } from '~/data/generated/model';
import { useTaskLogLabels } from '~/data/hooks/useTaskLogLabels';
import { BUTTON_SX } from '~/theme';

type Staged = { key: string; file: File; failed: boolean };

type Props = {
  onClose: () => void;
  onSave: (labelId: string | null, text: string) => Promise<TaskDetail>;
  onAttach: (file: File, logId: string | undefined) => Promise<unknown>;
};

export function LogUpdateDialog({ onClose, onSave, onAttach }: Props) {
  const { labels } = useTaskLogLabels();
  const [pickedLabelId, setPickedLabelId] = useState<string | null>(null);
  const labelId =
    pickedLabelId && labels.some((l) => l.id === pickedLabelId) ? pickedLabelId : null;
  const [draft, setDraft] = useState('');
  const [staged, setStaged] = useState<Staged[]>([]);
  const [rejected, setRejected] = useState<string[]>([]);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [savedLogId, setSavedLogId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);
  const logSaved = savedLogId !== null;
  const needsLabel = !logSaved && labelId === null && draft.trim().length > 0;
  const canSave =
    !saving && (logSaved ? staged.length > 0 : labelId !== null && draft.trim().length > 0);

  const close = () => {
    if (!saving) onClose();
  };

  const pick = (files: FileList | null) => {
    const errors: string[] = [];
    const accepted: Staged[] = [];
    for (const file of Array.from(files ?? [])) {
      const error = evidenceFileError(file);
      if (error) errors.push(error);
      else accepted.push({ key: crypto.randomUUID(), file, failed: false });
    }
    setRejected(errors);
    setStaged((current) => [...current, ...accepted]);
    if (fileInput.current) fileInput.current.value = '';
  };

  const save = async () => {
    if (!canSave) return;
    setSaving(true);
    setUploadError(null);
    let logId = savedLogId;
    if (logId === null) {
      const text = draft.trim();
      try {
        const detail = await onSave(labelId, text);
        logId = (
          detail.logs.find((l) => (l.label?.id ?? null) === labelId && l.text === text) ??
          detail.logs[0]
        )?.id;
      } catch {
        setSaving(false);
        return;
      }
      setSavedLogId(logId ?? '');
    }
    const failed: Staged[] = [];
    for (const item of staged) {
      try {
        await onAttach(item.file, logId || undefined);
      } catch {
        failed.push({ ...item, failed: true });
      }
    }
    setSaving(false);
    if (failed.length > 0) {
      setStaged(failed);
      setUploadError(
        `The log was saved, but ${failed.map((f) => f.file.name).join(', ')} could not be uploaded. Retry, or remove the file and cancel.`,
      );
      return;
    }
    onClose();
  };

  return (
    <Dialog open onClose={close} fullWidth maxWidth="sm" aria-labelledby="log-update-title">
      <DialogTitle id="log-update-title" sx={{ pr: 6 }}>
        Log update
      </DialogTitle>
      <IconButton
        aria-label="Close"
        onClick={close}
        disabled={saving}
        sx={{ position: 'absolute', right: 12, top: 12 }}
      >
        <CloseIcon fontSize="small" />
      </IconButton>

      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
        <Box sx={{ mt: 1 }}>
          <PillRadioGroup
            label="Label"
            required
            options={labels.map((label) => ({
              value: label.id,
              label: label.name,
              style: STATUS_COLOR[label.color],
            }))}
            value={labelId}
            onChange={setPickedLabelId}
            disabled={logSaved || saving}
            missingHint={needsLabel ? 'Pick a label to save this update.' : null}
          />
        </Box>
        <TextField
          multiline
          fullWidth
          minRows={4}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          disabled={logSaved || saving}
          placeholder="What happened? e.g. Called Schindler, quote promised by Friday."
          // eslint-disable-next-line jsx-a11y/no-autofocus -- the dialog exists to take this text
          autoFocus
        />
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '6px' }}>
          <Button
            size="small"
            color="inherit"
            startIcon={<AttachFileIcon />}
            onClick={() => fileInput.current?.click()}
            disabled={saving}
            sx={{ ...BUTTON_SX, color: 'text.secondary' }}
          >
            Attach evidence
          </Button>
          <input
            ref={fileInput}
            type="file"
            hidden
            multiple
            accept={EVIDENCE_ACCEPT}
            data-testid="evidence-input"
            onChange={(e) => pick(e.target.files)}
          />
          {staged.map((item) => (
            <Chip
              key={item.key}
              size="small"
              icon={<AttachFileIcon />}
              label={`${item.file.name} · ${formatFileSize(item.file.size)}`}
              color={item.failed ? 'error' : 'default'}
              variant="outlined"
              onDelete={
                saving
                  ? undefined
                  : () => setStaged((current) => current.filter((x) => x.key !== item.key))
              }
              sx={{ maxWidth: '100%' }}
            />
          ))}
        </Box>
        {[...rejected, ...(uploadError ? [uploadError] : [])].map((message) => (
          <Box key={message} role="alert" sx={{ fontSize: 12, color: 'error.main' }}>
            {message}
          </Box>
        ))}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        <Box component="span" sx={{ flex: 1, fontSize: 12, color: 'text.secondary' }}>
          Logged by you · {formatDate(new Date().toISOString())}
        </Box>
        <Button variant="outlined" onClick={close} disabled={saving} sx={BUTTON_SX}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={() => void save()}
          disabled={!canSave}
          startIcon={saving ? <CircularProgress size={14} color="inherit" /> : undefined}
          sx={BUTTON_SX}
        >
          {saving ? 'Saving…' : logSaved ? 'Retry upload' : 'Save log'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
